// src/components/tournament/Bracket.js

import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { ConnectLine, getBracketNodePositions } from "../../utils/layoutUtils";
import MatchBox from "./MatchBox";

// Layout constants
const MATCH_BOX_WIDTH = 168;
const HEADER_OFFSET = 40;
const CONTENT_PADDING = 20;

// Zoom constants
const MIN_SCALE = 0.3;
const MAX_SCALE = 2;
// Pinch-to-zoom sensitivity: scale change per pixel of finger-distance delta.
// Lower = calmer/less twitchy on mobile.
const PINCH_SENSITIVITY = 0.0025;

function Bracket({
    bracket,
    nodePositions,
    updateMatch,
    startMatch,
    unstartMatch,
    markFinished,
    undoFinished,
    finalizeSingleBye,
    onSwapPlayers,
    readOnly = false,
    hideBYEMatches = true,
    hideProgressIndicator = false, // Hide the floating progress indicator (used on mobile public viewer)
    onViewProfile = null,
    playerUserIdMap = null,
    onMatchDetailsClick = null, // Optional callback for viewing finished match details (used in public viewer)
    isDoubles = false, // If true, abbreviate last names in player display
    selfScoringEnabled = false, // If true, show SMS popup when starting matches
    raceSettings = null // { winnersRaceLength, losersRaceLength } — caps score entry
}) {
    // Filter out BYE matches if hideBYEMatches is enabled. Memoized so a parent
    // re-render that doesn't change the bracket doesn't rebuild this array (which
    // would otherwise invalidate every downstream useMemo keyed on it).
    const filteredBracket = useMemo(() => (
        hideBYEMatches
            ? bracket.filter(match => match.p1.name !== "BYE" && match.p2.name !== "BYE")
            : bracket
    ), [bracket, hideBYEMatches]);

    const totalMatches = bracket.length;
    const visibleMatches = filteredBracket.length;
    const hiddenCount = totalMatches - visibleMatches;

    // Recalculate positions for filtered bracket when hiding BYE matches
    const recalculatedPositions = useMemo(() => {
        if (hideBYEMatches && hiddenCount > 0) {
            // Calculate rounds for the filtered bracket
            const maxRound = Math.max(...filteredBracket.map(m => m.round || 0));
            return getBracketNodePositions(filteredBracket, maxRound);
        }
        return nodePositions;
    }, [hideBYEMatches, hiddenCount, filteredBracket, nodePositions]);

    const activePositions = recalculatedPositions;

    // State for drag navigation
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);

    // Momentum scrolling state
    const velocityRef = useRef({ x: 0, y: 0 });
    const lastTouchRef = useRef({ x: 0, y: 0, time: 0 });
    const momentumAnimationRef = useRef(null);

    // State for pinch-to-zoom
    const [scale, setScale] = useState(1);
    const [lastTouchDistance, setLastTouchDistance] = useState(null);
    const [showZoomControls, setShowZoomControls] = useState(false);

    // Show zoom controls only on desktop (1000px+ width) - mobile users can pinch-to-zoom
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1000;

    // Show zoom controls on desktop
    useEffect(() => {
        setShowZoomControls(isDesktop);
    }, [isDesktop]);

    // Cleanup momentum animation on unmount
    useEffect(() => {
        return () => {
            if (momentumAnimationRef.current) {
                cancelAnimationFrame(momentumAnimationRef.current);
            }
        };
    }, []);

    // Calculate distance between two touch points
    const getTouchDistance = (touches) => {
        if (touches.length < 2) return null;
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Touch event handlers for mobile pan and pinch-to-zoom
    const handleTouchStart = (e) => {
        // Don't interfere with buttons
        if (e.target.closest('button')) {
            return;
        }

        const scrollContainer = containerRef.current?.closest('.bracket-scroll-container');
        if (!scrollContainer) return;

        // Cancel any ongoing momentum animation
        if (momentumAnimationRef.current) {
            cancelAnimationFrame(momentumAnimationRef.current);
            momentumAnimationRef.current = null;
        }

        if (e.touches.length === 2) {
            // Pinch gesture starting
            e.preventDefault();
            const distance = getTouchDistance(e.touches);
            setLastTouchDistance(distance);
        } else if (e.touches.length === 1) {
            // Single touch - start panning (allow from anywhere in read-only mode)
            // In edit mode, don't pan from match boxes
            if (!readOnly && e.target.closest('.match-box')) {
                return;
            }
            setIsDragging(true);
            const touchX = e.touches[0].clientX;
            const touchY = e.touches[0].clientY;
            setDragStart({ x: touchX, y: touchY });
            setScrollStart({
                x: scrollContainer.scrollLeft,
                y: scrollContainer.scrollTop
            });
            // Initialize velocity tracking
            lastTouchRef.current = { x: touchX, y: touchY, time: Date.now() };
            velocityRef.current = { x: 0, y: 0 };
        }
    };

    const handleTouchMove = useCallback((e) => {
        const scrollContainer = containerRef.current?.closest('.bracket-scroll-container');
        if (!scrollContainer) return;

        if (e.touches.length === 2) {
            // Pinch gesture
            e.preventDefault();
            const distance = getTouchDistance(e.touches);
            if (lastTouchDistance && distance) {
                const delta = distance - lastTouchDistance;
                const scaleChange = delta * PINCH_SENSITIVITY;
                setScale(prev => Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev + scaleChange)));
                setLastTouchDistance(distance);
            }
        } else if (e.touches.length === 1 && isDragging) {
            // Single touch panning
            const touchX = e.touches[0].clientX;
            const touchY = e.touches[0].clientY;
            const deltaX = dragStart.x - touchX;
            const deltaY = dragStart.y - touchY;

            scrollContainer.scrollLeft = scrollStart.x + deltaX;
            scrollContainer.scrollTop = scrollStart.y + deltaY;

            // Calculate velocity for momentum scrolling
            const now = Date.now();
            const dt = now - lastTouchRef.current.time;
            if (dt > 0) {
                // Velocity is the change in touch position over time (pixels per ms)
                // We invert because scroll direction is opposite to touch direction
                velocityRef.current = {
                    x: (lastTouchRef.current.x - touchX) / dt,
                    y: (lastTouchRef.current.y - touchY) / dt
                };
                lastTouchRef.current = { x: touchX, y: touchY, time: now };
            }
        }
    }, [isDragging, dragStart, scrollStart, lastTouchDistance]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
        setLastTouchDistance(null);

        // Apply momentum scrolling
        const scrollContainer = containerRef.current?.closest('.bracket-scroll-container');
        if (!scrollContainer) return;

        let velocityX = velocityRef.current.x;
        let velocityY = velocityRef.current.y;

        // Only apply momentum if velocity is significant (more than 0.1 pixels/ms)
        const minVelocity = 0.1;
        if (Math.abs(velocityX) < minVelocity && Math.abs(velocityY) < minVelocity) {
            return;
        }

        // Friction coefficient - higher = faster deceleration
        const friction = 0.95;
        // Minimum velocity threshold to stop animation
        const stopThreshold = 0.05;

        const animateMomentum = () => {
            // Apply velocity to scroll position (multiply by ~16ms for smooth animation)
            scrollContainer.scrollLeft += velocityX * 16;
            scrollContainer.scrollTop += velocityY * 16;

            // Apply friction to slow down
            velocityX *= friction;
            velocityY *= friction;

            // Continue animation if velocity is still significant
            if (Math.abs(velocityX) > stopThreshold || Math.abs(velocityY) > stopThreshold) {
                momentumAnimationRef.current = requestAnimationFrame(animateMomentum);
            } else {
                momentumAnimationRef.current = null;
            }
        };

        momentumAnimationRef.current = requestAnimationFrame(animateMomentum);
    }, []);

    // Drag event handlers for desktop
    const handleMouseDown = (e) => {
        // Don't start dragging if clicking on a button
        if (e.target.closest('button')) {
            return;
        }

        // In edit mode, don't drag from match boxes
        if (!readOnly && e.target.closest('.match-box')) {
            return;
        }

        const scrollContainer = containerRef.current?.closest('.bracket-scroll-container');
        if (!scrollContainer) return;

        setIsDragging(true);
        setDragStart({
            x: e.clientX,
            y: e.clientY
        });
        setScrollStart({
            x: scrollContainer.scrollLeft,
            y: scrollContainer.scrollTop
        });
        e.preventDefault();
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        const scrollContainer = containerRef.current?.closest('.bracket-scroll-container');
        if (!scrollContainer) return;

        // Calculate scroll position based on mouse movement
        const deltaX = dragStart.x - e.clientX;
        const deltaY = dragStart.y - e.clientY;

        scrollContainer.scrollLeft = scrollStart.x + deltaX;
        scrollContainer.scrollTop = scrollStart.y + deltaY;
    }, [isDragging, dragStart.x, dragStart.y, scrollStart.x, scrollStart.y]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Zoom control handlers
    const handleZoomIn = () => {
        setScale(prev => Math.min(MAX_SCALE, prev + 0.2));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(MIN_SCALE, prev - 0.2));
    };

    const handleZoomReset = () => {
        setScale(1);
    };

    const handleFitToScreen = () => {
        const scrollContainer = containerRef.current?.closest('.bracket-scroll-container');
        if (!scrollContainer) return;

        const containerWidth = scrollContainer.clientWidth;
        const containerHeight = scrollContainer.clientHeight;
        // Use the unscaled content dimensions (dynamicWidth and svgHeight are calculated above)
        const contentWidth = dynamicWidth;
        const contentHeight = svgHeight;

        const scaleX = (containerWidth - 40) / contentWidth;
        const scaleY = (containerHeight - 40) / contentHeight;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min(scaleX, scaleY)));

        setScale(newScale);
    };

    // Add global mouse event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'grabbing';

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.cursor = 'default';
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Connector lines between matches. Memoized on the bracket + positions so pan/
    // zoom/drag state changes don't rebuild every <ConnectLine>.
    const connectors = useMemo(() => {
        const visibleMatchIds = new Set(filteredBracket.map(m => m.id));
        const lines = [];
        filteredBracket.forEach(m => {
            if (m.from) {
                m.from.forEach(src => {
                    // Only draw connectors if both source and destination are visible
                    if (src && visibleMatchIds.has(src) && activePositions[src] && activePositions[m.id]) {
                        lines.push(
                            <ConnectLine key={`${src}-${m.id}`} from={src} to={m.id} nodes={activePositions} />
                        );
                    }
                });
            }
        });
        return lines;
    }, [filteredBracket, activePositions]);

    const svgHeight = useMemo(() => {
        const maxY = Math.max(...Object.values(activePositions).map(pos => pos.y + pos.h), 400);
        return maxY + 20; // Add 20px bottom padding
    }, [activePositions]);

    // Center positions for the (invisible) winners/losers navigation anchors.
    const { winnersAnchorPosition, losersAnchorPosition } = useMemo(() => {
        const computeAnchor = (round1Matches) => {
            if (round1Matches.length === 0) return null;
            const positions = round1Matches.map(m => activePositions[m.id]).filter(Boolean);
            if (positions.length === 0) return null;
            const minY = Math.min(...positions.map(p => p.y));
            const maxY = Math.max(...positions.map(p => p.y + p.h));
            return { x: positions[0].x + (positions[0].w / 2), y: (minY + maxY) / 2 };
        };
        return {
            winnersAnchorPosition: computeAnchor(filteredBracket.filter(m => m.bracket === "W" && m.round === 1)),
            losersAnchorPosition: computeAnchor(filteredBracket.filter(m => m.bracket === "L" && m.round === 1)),
        };
    }, [filteredBracket, activePositions]);

    // Generate column headers with subtle underline style. Memoized on bracket + positions.
    const headers = useMemo(() => {
        const headers = [];
        const boxW = MATCH_BOX_WIDTH;

        // Subtle header style - underline instead of solid background
        const baseHeaderStyle = {
            position: 'absolute',
            width: boxW,
            textAlign: 'center',
            fontWeight: '700',
            fontSize: '12px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '6px 8px 8px',
            background: 'transparent',
            fontFamily: 'var(--font-mono)'
        };

        // Group matches by bracket and round
        const winners = filteredBracket.filter(m => m.bracket === "W");
        const losers = filteredBracket.filter(m => m.bracket === "L");
        const grandFinals = filteredBracket.filter(m => m.bracket === "GF");

        // Winners headers - use actual match positions instead of calculated positions
        const winnersRounds = [...new Set(winners.map(m => m.round))].sort((a, b) => a - b);
        winnersRounds.forEach((round) => {
            const label = `ROUND ${round}`;

            // Find all matches in this round and get their actual positions
            const roundMatches = winners.filter(m => m.round === round);
            const roundPositions = roundMatches.map(m => activePositions[m.id]).filter(Boolean);

            if (roundPositions.length > 0) {
                // Use the actual X position of the first match in this round
                const x = roundPositions[0].x;
                const topY = Math.min(...roundPositions.map(p => p.y));
                const headerY = Math.max(10, topY - HEADER_OFFSET);

                headers.push(
                    <div
                        key={`w-header-${round}`}
                        style={{
                            ...baseHeaderStyle,
                            left: x,
                            top: headerY,
                            color: 'var(--text-secondary)',
                            borderBottom: '2px solid var(--text-muted)'
                        }}
                    >
                        <span style={{ opacity: 0.6, marginRight: '4px' }}>W</span>
                        {label}
                    </div>
                );
            }
        });

        // Losers headers - use actual match positions instead of calculated positions
        const losersRounds = [...new Set(losers.map(m => m.round))].sort((a, b) => a - b);
        losersRounds.forEach((round) => {
            const label = `ROUND ${round}`;

            // Find all matches in this round and get their actual positions
            const roundMatches = losers.filter(m => m.round === round);
            const roundPositions = roundMatches.map(m => activePositions[m.id]).filter(Boolean);

            if (roundPositions.length > 0) {
                // Use the actual X position of the first match in this round
                const x = roundPositions[0].x;
                const topY = Math.min(...roundPositions.map(p => p.y));
                const headerY = Math.max(10, topY - HEADER_OFFSET);

                headers.push(
                    <div
                        key={`l-header-${round}`}
                        style={{
                            ...baseHeaderStyle,
                            left: x,
                            top: headerY,
                            color: 'var(--text-secondary)',
                            borderBottom: '2px solid var(--text-muted)'
                        }}
                    >
                        <span style={{ opacity: 0.6, marginRight: '4px' }}>L</span>
                        {label}
                    </div>
                );
            }
        });

        // Grand Finals headers - aligned with Winners Final
        if (grandFinals.length > 0) {
            grandFinals.forEach((match, idx) => {
                const isReset = match.id.includes('reset');
                const label = isReset ? 'GF RESET' : 'GRAND FINAL';

                // Find the position of this specific grand finals match
                const matchPosition = activePositions[match.id];
                if (matchPosition) {
                    const headerY = Math.max(10, matchPosition.y - HEADER_OFFSET);

                    headers.push(
                        <div
                            key={`gf-header-${idx}`}
                            style={{
                                ...baseHeaderStyle,
                                left: matchPosition.x,
                                top: headerY,
                                color: 'var(--text-secondary)',
                                borderBottom: '2px solid var(--text-muted)'
                            }}
                        >
                            <span style={{ marginRight: '4px' }}>{isReset ? '🔄' : '🏆'}</span>
                            {label}
                        </div>
                    );
                }
            });
        }

        return headers;
    }, [filteredBracket, activePositions]);

    // Calculate tournament progress for the progress indicator (memoized)
    const progress = useMemo(() => {
        // Filter out the grand final reset match - it's conditional and shouldn't count toward progress
        // (only played if the losers bracket player wins the first grand final)
        const relevantMatches = filteredBracket.filter(m => m.id !== "gf-1-reset");
        const totalMatches = relevantMatches.length;
        const completedMatches = relevantMatches.filter(m => m.finished).length;
        const inProgressMatches = relevantMatches.filter(m => m.started && !m.finished).length;
        const percentage = totalMatches > 0 ? Math.round((completedMatches / totalMatches) * 100) : 0;

        return { totalMatches, completedMatches, inProgressMatches, percentage };
    }, [filteredBracket]);

    // Calculate the required width based on the rightmost positioned element (memoized).
    const dynamicWidth = useMemo(() => {
        if (!activePositions || Object.keys(activePositions).length === 0) {
            return 2000; // smaller fallback width
        }
        let maxX = 0;
        Object.values(activePositions).forEach(position => {
            const rightEdge = position.x + position.w;
            if (rightEdge > maxX) {
                maxX = rightEdge;
            }
        });
        // Add extra width to account for right padding
        return maxX + CONTENT_PADDING;
    }, [activePositions]);

    // Zoom control button style
    const zoomButtonStyle = {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        border: '1px solid var(--border-color)',
        backgroundColor: 'var(--bg-secondary)',
        color: 'var(--text-primary)',
        fontSize: '18px',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'var(--shadow-sm)',
        touchAction: 'manipulation'
    };

    return (
        <>
            {/* Tournament Progress Indicator - hidden on mobile public viewer */}
            {!hideProgressIndicator && (
            <div
                className="bracket-progress"
                style={{
                    position: 'fixed',
                    top: '70px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 16px',
                    background: 'var(--bg-glass)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderRadius: '20px',
                    border: '1px solid var(--border-glass)',
                    boxShadow: 'var(--shadow-md)',
                    zIndex: 1200,
                    fontFamily: 'var(--font-mono)',
                    fontSize: '12px'
                }}
            >
                {/* Progress bar */}
                <div style={{
                    width: '100px',
                    height: '6px',
                    background: 'var(--bg-tertiary)',
                    borderRadius: '3px',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <div style={{
                        width: `${progress.percentage}%`,
                        height: '100%',
                        // Use the defined accent color. The previous gradient
                        // referenced undefined vars (--primary/--primary-light), so
                        // the fill rendered transparent — the bar looked empty even
                        // though its width was correct.
                        background: progress.percentage === 100
                            ? 'linear-gradient(90deg, var(--success), var(--bracket-finals))'
                            : 'var(--accent-primary)',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease',
                        animation: progress.inProgressMatches > 0 ? 'progressPulse 2s ease-in-out infinite' : 'none'
                    }} />
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                    <span style={{
                        color: 'var(--success)',
                        fontWeight: '600'
                    }}>
                        {progress.completedMatches}
                    </span>
                    <span style={{ opacity: 0.5 }}>/</span>
                    <span>{progress.totalMatches}</span>
                    {progress.inProgressMatches > 0 && (
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: 'var(--warning)',
                            marginLeft: '4px'
                        }}>
                            <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: 'var(--warning)',
                                animation: 'pulse 1.5s ease-in-out infinite'
                            }} />
                            {progress.inProgressMatches} live
                        </span>
                    )}
                </div>

                {/* Percentage */}
                <span style={{
                    fontWeight: '700',
                    color: progress.percentage === 100 ? 'var(--bracket-finals)' : 'var(--text-primary)',
                    minWidth: '40px',
                    textAlign: 'right'
                }}>
                    {progress.percentage}%
                </span>
            </div>
            )}

            {/* Zoom Controls - show on desktop, or show reset button if zoomed on mobile */}
            {(showZoomControls || (scale !== 1 && isDesktop)) && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '80px',
                        right: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        zIndex: 1500,
                        backgroundColor: 'var(--bg-card)',
                        padding: '8px',
                        borderRadius: '12px',
                        boxShadow: 'var(--shadow-md)'
                    }}
                >
                    <button
                        onClick={handleZoomIn}
                        style={zoomButtonStyle}
                        title="Zoom In"
                    >
                        +
                    </button>
                    <button
                        onClick={handleZoomOut}
                        style={zoomButtonStyle}
                        title="Zoom Out"
                    >
                        −
                    </button>
                    <button
                        onClick={handleFitToScreen}
                        style={{ ...zoomButtonStyle, fontSize: '12px' }}
                        title="Fit to Screen"
                    >
                        ⤢
                    </button>
                    <button
                        onClick={handleZoomReset}
                        style={{ ...zoomButtonStyle, fontSize: '11px' }}
                        title="Reset Zoom"
                    >
                        100%
                    </button>
                    <div style={{
                        fontSize: '10px',
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        marginTop: '2px'
                    }}>
                        {Math.round(scale * 100)}%
                    </div>
                </div>
            )}

            <div
                ref={containerRef}
                style={{
                    position: "relative",
                    // Multiply dimensions by scale so the scroll container knows the actual rendered size
                    // This ensures the scrollable area expands when zooming in
                    width: Math.max(dynamicWidth * scale + 40, window.innerWidth),
                    height: Math.max(svgHeight * scale + 40, window.innerHeight - 150),
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    transition: "margin-left 0.25s",
                    touchAction: 'none' // Prevent browser handling of touch gestures
                }}
                className="bracket-container"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
            {/* Inner container with transform for visual scaling */}
            <div
                style={{
                    position: 'absolute',
                    top: isDesktop ? 20 : 10,
                    left: isDesktop ? 20 : 10,
                    width: dynamicWidth,
                    height: svgHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left'
                }}
            >
            {/* Invisible Anchor Elements for Navigation */}
            {winnersAnchorPosition && (
                <div
                    id="winners-bracket-anchor"
                    style={{
                        position: 'absolute',
                        left: winnersAnchorPosition.x - 100,
                        top: winnersAnchorPosition.y - 100,
                        width: 1,
                        height: 1,
                        visibility: 'hidden'
                    }}
                />
            )}

            {losersAnchorPosition && (
                <div
                    id="losers-bracket-anchor"
                    style={{
                        position: 'absolute',
                        left: losersAnchorPosition.x - 100,
                        top: losersAnchorPosition.y - 100,
                        width: 1,
                        height: 1,
                        visibility: 'hidden'
                    }}
                />
            )}

            {/* Column Headers */}
            {headers}

            {/* Hidden matches indicator - only show in edit mode, not in read-only/public view */}
            {!readOnly && hideBYEMatches && hiddenCount > 0 && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    boxShadow: 'var(--shadow-sm)',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: 'var(--text-secondary)',
                    zIndex: 1400
                }}>
                    Showing {visibleMatches} of {totalMatches} matches ({hiddenCount} BYE hidden)
                </div>
            )}

            <svg
                width={dynamicWidth}
                height={svgHeight}
                style={{
                    position: "absolute",
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            >
                <defs>
                    <marker id="arrow" markerWidth="8" markerHeight="8" refX="8" refY="4" orient="auto" markerUnits="strokeWidth">
                        <path d="M0,0 L8,4 L0,8" fill="#999" />
                    </marker>
                </defs>
                {connectors}
            </svg>

            {filteredBracket.map(match => {
                const position = activePositions[match.id];

                if (!position) {
                    return null;
                }

                return (
                    <MatchBox
                        key={match.id}
                        match={match}
                        position={position}
                        updateMatch={updateMatch}
                        startMatch={startMatch}
                        unstartMatch={unstartMatch}
                        markFinished={markFinished}
                        undoFinished={undoFinished}
                        finalizeSingleBye={finalizeSingleBye}
                        bracket={filteredBracket}
                        fullBracket={bracket}
                        hideBYEMatches={hideBYEMatches}
                        onSwapPlayers={onSwapPlayers}
                        readOnly={readOnly}
                        onViewProfile={onViewProfile}
                        playerUserIdMap={playerUserIdMap}
                        selfScoringEnabled={selfScoringEnabled}
                        onMatchDetailsClick={onMatchDetailsClick}
                        isDoubles={isDoubles}
                        raceSettings={raceSettings}
                    />
                );
            })}
            </div>{/* Close inner scaled wrapper */}
            </div>{/* Close outer container */}
        </>
    );
}

// Memoized: props from App are stable values plus stable-identity callbacks, so the
// bracket only re-renders when the bracket data, positions, or display flags change.
export default React.memo(Bracket);